import { useState } from 'react'
import { Link } from 'react-router-dom'
import ClassifierMetadataFields, {
    ClassifierMetadata,
    isMetadataOverLimit
} from './ClassifierMetadataFields'
import { ManagedClassifierDetail } from './classifierVersions'

type Props = {
    detail: ManagedClassifierDetail
    saving: boolean
    onSave: (value: ClassifierMetadata) => void
}

function metadataOf(detail: ManagedClassifierDetail): ClassifierMetadata {
    return {
        displayName: detail.displayName,
        clientFacingName: detail.clientFacingName,
        shortDescription: detail.shortDescription,
        modality: detail.modality,
        valence: detail.valence
    }
}

/**
 * The unversioned metadata card.
 *
 * Form state lives here and is seeded from `detail` on mount, so the caller must key
 * this component on the classifier it's editing. That's what keeps a refetch —
 * cache-and-network revalidation fires several per visit — from overwriting an edit
 * in progress, which a seeding effect on the loaded record would do.
 */
export default function ClassifierDetailsForm({ detail, saving, onSave }: Props) {
    const [value, setValue] = useState<ClassifierMetadata>(() => metadataOf(detail))

    // Dirty against the loaded record rather than against a snapshot, so a save that
    // lands re-disables the button as soon as the refetched detail arrives.
    const saved = metadataOf(detail)
    const dirty =
        value.displayName !== saved.displayName ||
        value.clientFacingName !== saved.clientFacingName ||
        value.shortDescription !== saved.shortDescription ||
        value.modality !== saved.modality ||
        value.valence !== saved.valence

    return (
        <div className="space-y-4 rounded-lg border border-ink-200 bg-white p-4">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-ink-900">Details</h2>
                <div className="flex items-center gap-2">
                    {dirty && (
                        <Link
                            to="/admin/classifiers"
                            className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-semibold text-ink-700 hover:bg-ink-50"
                        >
                            Cancel
                        </Link>
                    )}
                    <button
                        type="button"
                        onClick={() => onSave(value)}
                        disabled={!dirty || saving || isMetadataOverLimit(value)}
                        className="rounded-lg bg-arwen-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-arwen-700 disabled:opacity-50"
                    >
                        {saving ? 'Saving…' : 'Save details'}
                    </button>
                </div>
            </div>
            <ClassifierMetadataFields value={value} onChange={setValue} overLimitAction="save" />
        </div>
    )
}
